/**
 * SidebarInner — the navigational content of the sidebar.
 *
 * Renders everything that was previously hardcoded inside the <aside> in
 * Layout.jsx.  Used in two contexts:
 *   1. Desktop: mounted inside Layout's <aside> (collapsed/expanded modes)
 *   2. Mobile/Tablet: mounted inside MobileDrawer (always expanded, no collapse button)
 *
 * Profile menu strategy
 * ─────────────────────
 * We deliberately avoid position:absolute/fixed for the profile menu.
 * Both the desktop sidebar (overflow:hidden + z-index stacking context) and
 * the MobileDrawer (transform:translateX animation, which breaks position:fixed
 * for descendants) make any floating approach unreliable across all contexts.
 *
 * Instead the profile panel is an INLINE expanding section rendered in the
 * normal document flow directly above the user-card trigger.  The scrollable
 * <nav> above it has flex:1 + overflow-y:auto, so it simply shrinks to give
 * the footer more room — no z-index, no portal, no positioning math needed.
 *
 * Props:
 *   collapsed          boolean   — show icon-only mode (desktop only)
 *   setCollapsed       fn|null   — toggle collapse state (desktop only)
 *   showCollapseButton boolean   — render the collapse toggle at the bottom
 *   onOpenCmdPalette   fn        — called when the search/⌘K button is pressed
 */

import React, { useMemo, useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/AuthContext";
import { usePerms } from "@/PermissionContext";
import usePublicConfig from "@/hooks/usePublicConfig";
import { GROUPS, navigationAllowed, navigationItemIsActive, defaultManufacturingTab } from "@/navigation";
import { UserAvatar } from "@/components/ui/UserAvatar";
import ProductBrand from "@/components/ProductBrand";
import {
  Search, LogOut, KeyRound, Crown, User,
  ChevronsLeft, ChevronsRight, ChevronUp,
} from "lucide-react";

import { cancelPagePreload, preloadPage, schedulePagePreload } from "@/utils/routePreload";

export default function SidebarInner({
  collapsed = false,
  setCollapsed = null,
  showCollapseButton = true,
  onOpenCmdPalette,
}) {
  const { user, logout } = useAuth();
  const { can, ready, isSuperAdmin, roleCodes, financeRestricted } = usePerms();
  const pubCfg = usePublicConfig();
  const nav = useNavigate();
  const location = useLocation();
  const defaultTab = defaultManufacturingTab(can);

  const [navFilter, setNavFilter]   = useState("");
  const [menuOpen, setMenuOpen]     = useState(false);
  const footerRef                   = useRef(null);

  const isLegacyAdmin = user?.role === "admin" || isSuperAdmin;

  // Close on Escape
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  // Close when clicking anywhere outside the footer
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e) => {
      if (footerRef.current && !footerRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [menuOpen]);

  // Close menu when sidebar collapses (collapsed mode has no panel space)
  useEffect(() => {
    if (collapsed) setMenuOpen(false);
  }, [collapsed]);

  const visibleGroups = useMemo(() => {
    const filterText = navFilter.trim().toLowerCase();
    return GROUPS
      .map(g => ({
        ...g,
        items: g.items
          .filter(it => !it.superOnly || isSuperAdmin)
          .filter(it => !it.migration || pubCfg.migration_ui_enabled)
          .filter(it => !it.hideInProduction || !pubCfg.is_production)
          .filter(it => ready && navigationAllowed(it, can, roleCodes, financeRestricted))
          .filter(it => !filterText || it.label.toLowerCase().includes(filterText)),
      }))
      .filter(g => g.items.length > 0);
  }, [ready, roleCodes, navFilter, can, isSuperAdmin, financeRestricted, pubCfg.migration_ui_enabled, pubCfg.is_production]);

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    nav("/login");
  };

  const handleNav = (path) => {
    setMenuOpen(false);
    nav(path);
  };

  return (
    <>
      {/* ── Brand ─────────────────────────────────────────────────── */}
      <div data-testid="sidebar-product-brand" style={{
        padding: collapsed ? "18px 12px" : "18px 20px",
        display: "flex", alignItems: "center", gap: 12,
        borderBottom: "1px solid var(--flg-border)",
        // Preserve the former 32px tile / 39px two-line brand row heights.
        minHeight: collapsed ? 69 : 76,
        flexShrink: 0,
      }}>
        <ProductBrand square={collapsed} width={collapsed ? 32 : 106} />
        {!collapsed && (
          <div style={{ minWidth: 0, overflow: "hidden" }}>
            <div style={{ fontSize: 11, color: "var(--flg-fg-muted)", fontFamily: "var(--flg-font-mono)" }}>Manufacturing ERP</div>
          </div>
        )}
      </div>

      {/* ── Search / Command palette trigger (expanded) ───────────── */}
      {!collapsed && (
        <div style={{ padding: "12px 12px 6px", flexShrink: 0 }}>
          <button
            onClick={onOpenCmdPalette}
            data-testid="sidebar-cmdk"
            style={{
              width: "100%",
              display: "flex", alignItems: "center", gap: 10,
              padding: "8px 12px",
              background: "var(--flg-bg-subtle)",
              border: "1px solid var(--flg-border)",
              borderRadius: "var(--flg-radius-md)",
              cursor: "pointer",
              color: "var(--flg-fg-muted)",
              fontSize: 13,
              transition: "all 120ms",
            }}
            onMouseEnter={e => e.currentTarget.style.background = "var(--flg-bg-muted)"}
            onMouseLeave={e => e.currentTarget.style.background = "var(--flg-bg-subtle)"}
          >
            <Search size={14} />
            <span style={{ flex: 1, textAlign: "left" }}>Quick jump…</span>
            <span className="f-kbd" style={{ fontSize: 10 }}>⌘K</span>
          </button>
        </div>
      )}

      {/* ── Search icon (collapsed mode) ──────────────────────────── */}
      {collapsed && (
        <div style={{ padding: "12px", flexShrink: 0 }}>
          <button
            onClick={onOpenCmdPalette}
            className="f-btn-icon"
            title="Search (⌘K)"
            data-testid="sidebar-cmdk-collapsed"
            style={{ width: 40, height: 40, margin: "0 auto", display: "flex" }}
          >
            <Search size={16} />
          </button>
        </div>
      )}

      {/* ── Nav filter typeahead (expanded) ───────────────────────── */}
      {!collapsed && (
        <div style={{ padding: "0 12px 6px", flexShrink: 0 }}>
          <input
            value={navFilter}
            onChange={e => setNavFilter(e.target.value)}
            placeholder="Filter menu…"
            data-testid="sidebar-filter"
            style={{
              width: "100%",
              height: 30,
              padding: "0 10px",
              background: "transparent",
              border: "1px solid transparent",
              borderRadius: 6,
              fontSize: 12,
              color: "var(--flg-fg)",
              outline: "none",
            }}
            onFocus={e => { e.target.style.borderColor = "var(--flg-border)"; e.target.style.background = "var(--flg-bg-subtle)"; }}
            onBlur={e => { e.target.style.borderColor = "transparent"; e.target.style.background = "transparent"; }}
          />
        </div>
      )}

      {/* ── Navigation groups ─────────────────────────────────────── */}
      <nav style={{ flex: 1, overflowY: "auto", padding: "4px 8px 12px", minHeight: 0 }}>
        {visibleGroups.map((g, gi) => (
          <div key={g.label || `g${gi}`} style={{ marginBottom: 4 }}>
            {g.label && !collapsed && (
              <div style={{
                padding: "12px 12px 4px",
                fontSize: 10,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: "var(--flg-fg-muted)",
              }}>{g.label}</div>
            )}
            {g.label && collapsed && gi > 0 && (
              <div style={{ height: 1, background: "var(--flg-border)", margin: "8px 12px" }} />
            )}
            {g.items.map(it => {
              const isActive = navigationItemIsActive(it, location, defaultTab);
              return (
              <Link
                key={it.to}
                to={it.to}
                onPointerEnter={event => {
                  if (event.pointerType === "mouse") schedulePagePreload(it.to);
                }}
                onPointerLeave={cancelPagePreload}
                onFocus={() => preloadPage(it.to, { speculative: true })}
                aria-current={isActive ? "page" : undefined}
                data-testid={it.testid}
                title={collapsed ? it.label : undefined}
                className={`flg-nav-item ${isActive ? "flg-nav-item--active" : ""}`}
                style={{
                  display: "flex", alignItems: "center",
                  gap: collapsed ? 0 : 10,
                  padding: collapsed ? "8px" : "8px 10px",
                  margin: collapsed ? "2px 6px" : "1px 4px",
                  justifyContent: collapsed ? "center" : "flex-start",
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: isActive ? 500 : 400,
                  color: isActive ? "var(--flg-primary-hover)" : "var(--flg-fg-body)",
                  background: isActive ? "var(--flg-primary-soft)" : "transparent",
                  textDecoration: "none",
                  transition: "all 100ms",
                  position: "relative",
                }}
              >
                <it.icon size={16} strokeWidth={1.75} />
                {!collapsed && (
                  <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {it.label}
                  </span>
                )}
              </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {/* ── Footer: profile panel (inline) + user-card trigger ──────── */}
      {/*
        The profile panel is part of the normal document flow — it lives
        directly above the trigger button in the same flex column.  No
        position:fixed/absolute, no portal, no z-index arithmetic.  The <nav>
        above (flex:1 + overflow-y:auto + minHeight:0) shrinks automatically
        to make room, so the panel never overlaps anything.
      */}
      <div
        ref={footerRef}
        style={{ borderTop: "1px solid var(--flg-border)", flexShrink: 0 }}
      >
        {/* ── Inline profile panel ─────────────────────────────────── */}
        {menuOpen && !collapsed && (
          <div style={{
            borderBottom: "1px solid var(--flg-border)",
            background: "var(--flg-bg-elevated)",
          }}>
            {/* User identity */}
            <div style={{
              padding: "14px 16px 10px",
              display: "flex", alignItems: "center", gap: 10,
            }}>
              <UserAvatar user={user} size="sm" />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{
                  fontSize: 13, fontWeight: 600, color: "var(--flg-fg)",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>
                  {user?.name}
                </div>
                <div style={{
                  fontSize: 11, color: "var(--flg-fg-muted)",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  marginTop: 1,
                }}>
                  {user?.email}
                </div>
              </div>
            </div>

            {/* Divider */}
            <div style={{ height: 1, background: "var(--flg-border)", margin: "0 12px" }} />

            {/* Menu items */}
            <div style={{ padding: "4px 0" }}>
              <ProfileMenuItem
                icon={User}
                label="My Profile"
                onClick={() => handleNav("/profile")}
                testid="profile-menu-item"
              />
              <ProfileMenuItem
                icon={KeyRound}
                label="Change Password"
                onClick={() => handleNav("/change-password")}
                testid="change-password-menu-item"
              />
            </div>

            <div style={{ height: 1, background: "var(--flg-border)", margin: "0 12px" }} />

            <div style={{ padding: "4px 0" }}>
              <ProfileMenuItem
                icon={LogOut}
                label="Sign Out"
                onClick={handleLogout}
                danger
                testid="logout-menu-item"
              />
            </div>
          </div>
        )}

        {/* ── User card / trigger ───────────────────────────────── */}
        <div style={{ padding: collapsed ? "10px 6px" : "10px 12px" }}>
          {!collapsed ? (
            /* Expanded: full card with name, role, and chevron */
            <button
              data-testid="current-user-name"
              onClick={() => setMenuOpen(o => !o)}
              style={{
                width: "100%",
                display: "flex", alignItems: "center", gap: 10,
                padding: "6px 8px", borderRadius: 8,
                background: menuOpen ? "var(--flg-bg-subtle)" : "transparent",
                border: 0, cursor: "pointer",
                transition: "background 100ms",
                textAlign: "left",
              }}
              onMouseEnter={e => { if (!menuOpen) e.currentTarget.style.background = "var(--flg-bg-subtle)"; }}
              onMouseLeave={e => { if (!menuOpen) e.currentTarget.style.background = "transparent"; }}
            >
              <UserAvatar user={user} size="md" />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{
                  display: "flex", alignItems: "center", gap: 4,
                  fontSize: 13, fontWeight: 500, color: "var(--flg-fg)",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>
                  {isSuperAdmin && <Crown size={11} style={{ color: "#d97706", flexShrink: 0 }} data-testid="super-admin-badge" />}
                  {user?.name}
                </div>
                <div className="f-caption" data-testid="current-user-role" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  {isSuperAdmin ? "Super admin" : user?.role}
                </div>
              </div>
              <ChevronUp
                size={13}
                style={{
                  color: "var(--flg-fg-muted)",
                  flexShrink: 0,
                  transform: menuOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 150ms",
                }}
              />
            </button>
          ) : (
            /* Collapsed: avatar taps go straight to /profile */
            <div style={{ display: "flex", justifyContent: "center", padding: "4px 0" }}>
              <button
                data-testid="current-user-name"
                onClick={() => handleNav("/profile")}
                title={user?.name}
                style={{ background: "transparent", border: 0, cursor: "pointer", padding: 0 }}
              >
                <UserAvatar user={user} size="md" />
              </button>
            </div>
          )}

          {/* Collapse toggle — desktop only */}
          {showCollapseButton && setCollapsed && (
            <button
              onClick={() => setCollapsed(c => !c)}
              data-testid="sidebar-collapse"
              title="Toggle sidebar (⌘B)"
              style={{
                width: "100%", marginTop: 8, height: 28,
                display: "flex", alignItems: "center", justifyContent: "center",
                gap: 4,
                background: "transparent", border: "1px solid var(--flg-border)",
                borderRadius: 6, cursor: "pointer",
                color: "var(--flg-fg-muted)", fontSize: 11,
              }}
              onMouseEnter={e => e.currentTarget.style.background = "var(--flg-bg-subtle)"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              {collapsed ? <ChevronsRight size={13} /> : <><ChevronsLeft size={13} /> Collapse</>}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

function ProfileMenuItem({ icon: Icon, label, onClick, danger, testid }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: "100%", display: "flex", alignItems: "center", gap: 10,
        padding: "9px 16px",
        background: hovered
          ? (danger ? "rgba(239,68,68,0.07)" : "var(--flg-bg-subtle)")
          : "transparent",
        border: 0, cursor: "pointer",
        fontSize: 13, fontWeight: 400,
        color: danger ? "var(--flg-danger)" : "var(--flg-fg-body)",
        textAlign: "left",
        transition: "background 80ms",
      }}
    >
      <Icon
        size={15}
        strokeWidth={1.75}
        style={{ flexShrink: 0, color: danger ? "var(--flg-danger)" : "var(--flg-fg-muted)" }}
      />
      {label}
    </button>
  );
}
